import { format } from 'date-fns';
import {
  connectResponseSchema,
  createContactResponseSchema,
  submitApplicationResponseSchema,
  contactCenterAvailabilityResponseSchema,
  scheduleAppointmentResponseSchema,
  connectCallResponseSchema,
  availabilityResponseSchema,
  prospectVerifyResponseSchema,
  connectKbaResponseSchema,
  verifyKbaResponseSchema,
  partnerPreloadResponseSchema,
  startAutoEchelonResponseSchema,
  bffAutoEchelonStatusResponseSchema,
  leadDetailsResponseSchema,
  successPageResponseSchema,
  type ConnectResponse,
  type CreateContactResponse,
  type SubmitApplicationResponse,
  type ContactCenterAvailabilityResponse,
  type ScheduleAppointmentResponse,
  type ConnectCallResponse,
  type AvailabilityResponse,
  type ProspectVerifyResponse,
  type ConnectKbaResponse,
  type VerifyKbaResponse,
  type PartnerData,
  type StartAutoEchelonResponse,
  type GetOpportunityStatusResponse,
  type LeadDetailsData,
  type SuccessPageResponse,
} from '../types/api';
import { formatE164 } from '../utils/validation';
import { bffSessionSchema, type BffClient, type BffSession } from './bff';
import type { ILeadsService } from './leads';

const ACCEPT_QUALIFICATION_REJECTION = (status: number): boolean => (status >= 200 && status < 300) || status === 422;
const NO_CACHE_HEADERS = { 'Cache-Control': 'no-cache', Pragma: 'no-cache' };
const THANK_YOU_FALLBACK: SuccessPageResponse = { status: { code: 200, message: 'Success' } };
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

type PersonalInfo = {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  dateOfBirth?: string;
};

type AddressInfo = { street: string; city: string; state: string; zip: string };

type ApplicationProfile = {
  loanAmount: number | undefined;
  loanPurpose: string | undefined;
  employmentStatus: string | undefined;
  employmentFrequency: string | undefined;
  educationLevel: string | undefined;
  annualIncome: number | undefined;
  propertyStatus: string | undefined;
};

const definedOnly = (fields: Record<string, unknown>): Record<string, unknown> => {
  return Object.entries(fields).reduce<Record<string, unknown>>((acc, [key, value]) => {
    return value === undefined ? acc : { ...acc, [key]: value };
  }, {});
};

const toProfileBody = (profile: ApplicationProfile): Record<string, unknown> => definedOnly({
  loanAmount: profile.loanAmount,
  loanPurpose: profile.loanPurpose,
  employmentStatus: profile.employmentStatus,
  employmentPayFrequency: profile.employmentFrequency,
  educationLevel: profile.educationLevel,
  annualIncome: profile.annualIncome,
  propertyStatus: profile.propertyStatus,
});

const toPersonalInfoBody = (info: PersonalInfo) => ({
  firstName: info.firstName,
  lastName: info.lastName,
  email: info.email,
  phone: info.phone || '',
  dateOfBirth: info.dateOfBirth || '',
});

const toAddressInfoBody = (address: AddressInfo) => ({
  street: address.street,
  city: address.city,
  state: address.state,
  zipCode: address.zip,
});

const toLastFourSsn = (ssnLast4: string): string => (ssnLast4 || '0000').padStart(4, '0').slice(-4);

const toAvailabilityDate = (dateInput: string): string => {
  if (ISO_DATE_PATTERN.test(dateInput)) return dateInput;
  const date = new Date(dateInput);
  if (isNaN(date.getTime())) {
    throw new Error(`Invalid date format: ${dateInput}. Expected yyyy-MM-dd format.`);
  }
  return format(date, 'yyyy-MM-dd');
};

export class BffLeadsService implements ILeadsService {
  readonly serverManagedSession = true;

  constructor(private readonly client: BffClient) {}

  restoreSession(): Promise<BffSession> {
    return this.client.ensureSession();
  }

  resetSession(): Promise<BffSession> {
    return this.client.resetSession();
  }

  async claimLegacySession(hubspotRecordId: string, spinwheelId: string): Promise<BffSession> {
    const body = await this.client.post('/leads/claim', { hubspotRecordId, spinwheelId });
    return bffSessionSchema.parse(body);
  }

  async connectBySms(
    phoneNumber: string,
    email: string,
    dateOfBirth: string,
    loanAmount: number | undefined,
    loanPurpose: string | undefined,
    employmentStatus: string | undefined,
    employmentFrequency: string | undefined,
    educationLevel: string | undefined,
    annualIncome: number | undefined,
    propertyStatus: string | undefined,
    _ipAddress: string,
    marketingParams: Record<string, string>
  ): Promise<ConnectResponse> {
    const body = await this.client.post('/leads/connect', {
      email,
      phone: formatE164(phoneNumber),
      dateOfBirth,
      ...toProfileBody({ loanAmount, loanPurpose, employmentStatus, employmentFrequency, educationLevel, annualIncome, propertyStatus }),
      marketingParams,
    });
    return connectResponseSchema.parse(body);
  }

  async verifyCode(
    code: string,
    _phoneNumber: string,
    _spinwheelId: string,
    _email: string,
    _hubspotRecordId: string,
    promoSmsConsent?: boolean,
    promoSmsConsentDateTime?: string
  ): Promise<ConnectResponse> {
    const body = await this.client.post('/leads/verify', {
      code,
      ...(promoSmsConsent === true && { promoSmsConsent }),
      ...(promoSmsConsentDateTime !== undefined && { promoSmsConsentDateTime }),
    });
    return connectResponseSchema.parse(body);
  }

  async createContact(): Promise<CreateContactResponse> {
    const body = await this.client.post('/leads/create', {}, { validateStatus: ACCEPT_QUALIFICATION_REJECTION });
    return createContactResponseSchema.parse(body);
  }

  async createContactExperian(
    email: string,
    firstName: string,
    lastName: string,
    street: string,
    city: string,
    state: string,
    zip: string,
    dateOfBirth?: string,
    phone?: string
  ): Promise<CreateContactResponse> {
    const body = await this.client.post('/leads/experian', {
      personalInfo: toPersonalInfoBody({ firstName, lastName, email, phone, dateOfBirth }),
      addressInfo: toAddressInfoBody({ street, city, state, zip }),
    });
    return createContactResponseSchema.parse(body);
  }

  async createManualLead(
    email: string,
    firstName: string,
    lastName: string,
    street: string,
    city: string,
    state: string,
    zip: string,
    offerCode?: string,
    phone?: string,
    dateOfBirth?: string
  ): Promise<CreateContactResponse> {
    const body = await this.client.post('/leads/manual', {
      personalInfo: toPersonalInfoBody({ firstName, lastName, email, phone, dateOfBirth }),
      addressInfo: toAddressInfoBody({ street, city, state, zip }),
      ...(offerCode ? { offerCode } : {}),
    });
    return createContactResponseSchema.parse(body);
  }

  async prospectVerify(
    email: string,
    firstName: string,
    lastName: string,
    street: string,
    city: string,
    state: string,
    zip: string,
    offerCode: string,
    phone?: string,
    dateOfBirth?: string
  ): Promise<ProspectVerifyResponse> {
    const body = await this.client.post('/leads/prospect-verify', {
      personalInfo: toPersonalInfoBody({ firstName, lastName, email, phone, dateOfBirth }),
      addressInfo: toAddressInfoBody({ street, city, state, zip }),
      offerCode,
    });
    return prospectVerifyResponseSchema.parse(body);
  }

  async connectKba(
    email: string,
    phone: string,
    firstName: string,
    lastName: string,
    dateOfBirth: string,
    ssn: string,
    address: { street: string; city: string; state: string; zipCode: string },
    _ipAddress?: string,
    marketingParams?: Record<string, string>
  ): Promise<ConnectKbaResponse> {
    const body = await this.client.post('/leads/kba/connect', {
      email,
      phone: formatE164(phone),
      firstName,
      lastName,
      dateOfBirth,
      ssn,
      address,
      ...(marketingParams !== undefined && { marketingParams }),
    });
    return connectKbaResponseSchema.parse(body);
  }

  async verifyKba(
    _spinwheelId: string,
    _hubspotRecordId: string,
    answers: Record<string, string>
  ): Promise<VerifyKbaResponse> {
    const body = await this.client.post('/leads/kba/verify', { answers });
    return verifyKbaResponseSchema.parse(body);
  }

  async submitApplication(
    email: string,
    ssnLast4: string,
    loanAmount: number | undefined,
    loanPurpose: string | undefined,
    employmentStatus: string | undefined,
    employmentFrequency: string | undefined,
    educationLevel: string | undefined,
    annualIncome: number | undefined,
    propertyStatus: string | undefined
  ): Promise<SubmitApplicationResponse> {
    const body = await this.client.post('/leads/offers', {
      ...(email ? { email } : {}),
      sensitiveLastFourSsn: toLastFourSsn(ssnLast4),
      ...toProfileBody({ loanAmount, loanPurpose, employmentStatus, employmentFrequency, educationLevel, annualIncome, propertyStatus }),
    });
    return submitApplicationResponseSchema.parse(body);
  }

  async getPartnerPreload(partnerId: string): Promise<PartnerData> {
    const body = await this.client.get(`/partners/${encodeURIComponent(partnerId)}`);
    return partnerPreloadResponseSchema.parse(body).data;
  }

  async getLeadDetails(leadUniqueId: string): Promise<LeadDetailsData> {
    const body = await this.client.get(`/leads/resume/${encodeURIComponent(leadUniqueId)}`);
    return leadDetailsResponseSchema.parse(body).data;
  }

  async getContactCenterAvailability(): Promise<ContactCenterAvailabilityResponse> {
    const body = await this.client.get('/contact-center');
    return contactCenterAvailabilityResponseSchema.parse(body);
  }

  async getAvailability(date: string): Promise<AvailabilityResponse> {
    const body = await this.client.get(`/availability?date=${toAvailabilityDate(date)}`);
    return availabilityResponseSchema.parse(body);
  }

  async scheduleAppointment(_hubspotRecordId: string, meetingCallTime: string): Promise<ScheduleAppointmentResponse> {
    const body = await this.client.post('/appointment', { meetingCallTime });
    return scheduleAppointmentResponseSchema.parse(body);
  }

  async connectCall(): Promise<ConnectCallResponse> {
    const body = await this.client.post('/call', {});
    return connectCallResponseSchema.parse(body);
  }

  async successPage(_hubspotRecordId: string, variantId: string): Promise<SuccessPageResponse> {
    try {
      const body = await this.client.post('/thank-you', { availability: variantId });
      return successPageResponseSchema.parse(body);
    } catch {
      return THANK_YOU_FALLBACK;
    }
  }

  async startAutoEchelon(): Promise<StartAutoEchelonResponse> {
    const body = await this.client.post('/auto-echelon', {});
    return startAutoEchelonResponseSchema.parse(body);
  }

  async getOpportunityStatus(): Promise<GetOpportunityStatusResponse> {
    const body = await this.client.get('/auto-echelon/status', { params: { _t: Date.now() }, headers: NO_CACHE_HEADERS });
    const parsed = bffAutoEchelonStatusResponseSchema.parse(body);
    return {
      status: parsed.status,
      completed: parsed.completed,
      succeeded: parsed.succeeded,
      aeClientLink: parsed.redirectUrl ?? null,
      ...(parsed.redirectUrl ? { redirectUrl: parsed.redirectUrl } : {}),
    };
  }
}
